const DEFAULT_FULL_PLOTLY_URL = '/static/libs/plotly-3.0.1.min.js';
const FULL_PLOTLY_SCRIPT_ATTRIBUTE = 'data-askfelix-full-plotly';

let fullPlotlyPromise = null;

function normalizeTraceType(trace) {
  if (!trace || typeof trace !== 'object') return '';
  return String(trace.type || 'scatter').trim().toLowerCase();
}

function registeredTraceTypes(plotly) {
  try {
    const schema =
      plotly &&
      plotly.PlotSchema &&
      typeof plotly.PlotSchema.get === 'function' &&
      plotly.PlotSchema.get();
    const traces = schema && schema.traces;
    if (!traces || typeof traces !== 'object') return null;
    return new Set(Object.keys(traces).map(type => String(type).toLowerCase()));
  } catch (_error) {
    return null;
  }
}

export function getUnsupportedPlotlyTraceTypes(traces, plotly) {
  if (!Array.isArray(traces) || traces.length === 0) return [];
  const registered = registeredTraceTypes(plotly);
  if (!registered) {
    return Array.from(new Set(traces.map(normalizeTraceType).filter(Boolean)));
  }
  return Array.from(
    new Set(
      traces
        .map(normalizeTraceType)
        .filter(type => type && !registered.has(type))
    )
  );
}

function resolveLoadedPlotly(traces, getPlotly, resolve, reject) {
  const plotly = getPlotly();
  if (!plotly || typeof plotly.newPlot !== 'function') {
    reject(new Error('The full Plotly renderer loaded without exposing Plotly.newPlot.'));
    return;
  }
  if (typeof plotly.setPlotConfig === 'function') {
    plotly.setPlotConfig({ topojsonURL: '/static/libs/topojson/' });
  }
  const unsupported = getUnsupportedPlotlyTraceTypes(traces, plotly);
  if (unsupported.length) {
    reject(
      new Error(
        `The Plotly renderer does not support trace type${unsupported.length === 1 ? '' : 's'}: ` +
          unsupported.join(', ')
      )
    );
    return;
  }
  resolve(plotly);
}

export function ensurePlotlySupportsTraces(
  traces,
  {
    documentRef = typeof document !== 'undefined' ? document : null,
    getPlotly = () => (typeof window !== 'undefined' ? window.Plotly : undefined),
    fullPlotlyUrl = DEFAULT_FULL_PLOTLY_URL,
  } = {}
) {
  const current = getPlotly();
  const unsupported = getUnsupportedPlotlyTraceTypes(traces, current);
  if (
    current &&
    typeof current.newPlot === 'function' &&
    unsupported.length === 0
  ) {
    if (typeof current.setPlotConfig === 'function') {
      current.setPlotConfig({ topojsonURL: '/static/libs/topojson/' });
    }
    return Promise.resolve(current);
  }
  if (!documentRef || !documentRef.head || typeof documentRef.createElement !== 'function') {
    return Promise.reject(
      new Error(
        unsupported.length
          ? `Plotly does not support trace type${unsupported.length === 1 ? '' : 's'}: ${unsupported.join(', ')}`
          : 'Plotly is not available on this page.'
      )
    );
  }
  if (fullPlotlyPromise) return fullPlotlyPromise;

  fullPlotlyPromise = new Promise((resolve, reject) => {
    let script = documentRef.querySelector(
      `script[${FULL_PLOTLY_SCRIPT_ATTRIBUTE}="true"]`
    );
    if (!script) {
      const existingScripts = Array.from(documentRef.scripts || []);
      script = existingScripts.find(candidate => {
        try {
          return new URL(candidate.src, documentRef.baseURI).pathname === fullPlotlyUrl;
        } catch (_error) {
          return false;
        }
      });
      // If a previously loaded full build was overwritten by the basic vendor
      // bundle, execute it again. An in-flight build is reused below.
      if (script && script.getAttribute('data-loaded') === 'true') {
        script = null;
      }
    }
    const finish = () => resolveLoadedPlotly(traces, getPlotly, resolve, reject);
    const fail = () => {
      reject(new Error(`Could not load the full Plotly renderer from ${fullPlotlyUrl}.`));
    };

    if (script && script.getAttribute('data-loaded') === 'true') {
      finish();
      return;
    }
    if (!script) {
      script = documentRef.createElement('script');
      script.src = fullPlotlyUrl;
      script.async = true;
      script.setAttribute(FULL_PLOTLY_SCRIPT_ATTRIBUTE, 'true');
    }
    script.addEventListener(
      'load',
      () => {
        script.setAttribute('data-loaded', 'true');
        finish();
      },
      { once: true }
    );
    script.addEventListener('error', fail, { once: true });
    if (!script.parentNode) {
      documentRef.head.appendChild(script);
    }
  }).catch(error => {
    // A transient asset error should not poison every later chart render.
    fullPlotlyPromise = null;
    throw error;
  });
  return fullPlotlyPromise;
}
